/**
 * The edge function calls every page shares. Each throws an Error carrying
 * the function's own `error` message, which is written for the client.
 */
import { FUNCTIONS_URL, SUPABASE_ANON_KEY } from "../config.js";

const AUTH_HEADERS = { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` };

async function readJson(res) {
  return res.json().catch(() => ({}));
}

/** GET a function with query parameters. `auth` adds the anon key headers. */
export async function getFunction(name, params = {}, { auth = false, fallbackError } = {}) {
  const query = new URLSearchParams(params).toString();
  const res = await fetch(`${FUNCTIONS_URL}/${name}${query ? `?${query}` : ""}`, auth ? { headers: AUTH_HEADERS } : undefined);
  const body = await readJson(res);
  if (!res.ok) throw new Error(body.error || fallbackError || `Request failed (${res.status})`);
  return body;
}

/** POST JSON to a function. `auth` adds the anon key headers. */
export async function postFunction(name, payload, { auth = false, fallbackError } = {}) {
  const res = await fetch(`${FUNCTIONS_URL}/${name}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(auth ? AUTH_HEADERS : {}) },
    body: JSON.stringify(payload),
  });
  const body = await readJson(res);
  if (!res.ok || body.error) throw new Error(body.error || fallbackError || `Request failed (${res.status})`);
  return body;
}

/**
 * A one-time upload link into the request's folder. `generate-upload-url`
 * has the last word on the name (and numbers a taken one `-2`, `-3`…), so
 * only the link is used.
 */
export async function requestSignedUrl(token, fileName, folderPrefix) {
  const body = await postFunction(
    "generate-upload-url",
    { token, fileName, ...(folderPrefix ? { folderPrefix } : {}) },
    { fallbackError: "Could not get upload link" },
  );
  return body.signedUrl;
}

/** PUT a file to a signed upload link. */
export async function uploadToSignedUrl(signedUrl, file, type) {
  const res = await fetch(signedUrl, {
    method: "PUT",
    headers: { "Content-Type": type || file.type || "application/octet-stream" },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

/** Fire-and-forget tracking calls ("opened"): a failure never reaches the client. */
export async function track(name, token) {
  if (!token) return;
  try {
    await fetch(`${FUNCTIONS_URL}/${name}?token=${encodeURIComponent(token)}`);
  } catch (error) {
    console.warn(`${name} failed`, error);
  }
}

/** Every query parameter on the current address, for `?agent=` and the rest. */
export function urlParams(search = window.location.search) {
  const out = {};
  new URLSearchParams(search).forEach((value, key) => {
    out[key] = value;
  });
  return out;
}

export function uuid() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return String(Date.now()) + Math.random().toString(16).slice(2);
}

/**
 * Creates the request a public form link stands for — `submit-public-form`,
 * the only thing that mints a request from a public link. Called when the
 * visitor commits (Submit), never when they merely open the link. Resolves
 * to `{ token, signUrl }`.
 */
export async function createPublicRequest({ slug, answers = {}, plans = [], params, turnstileToken = "", idempotencyKey, startedAt = 0, website = "" }) {
  const body = await postFunction(
    "submit-public-form",
    {
      slug,
      answers,
      plans,
      params: params || urlParams(),
      turnstileToken,
      idempotencyKey: idempotencyKey || uuid(),
      startedAt,
      website,
    },
    { fallbackError: "Could not submit." },
  );
  if (!body.token) throw new Error(body.error || "Could not submit.");
  return { token: body.token, signUrl: body.signUrl };
}
